import { RefObject, useEffect, useRef, useState } from 'react';

/**
 * Hook to track listing views when they appear in the viewport.
 */
export function useIntersectionObserver(
  handleListingView: (isElementVisibleAndFirstAppearance: boolean) => void,
) {
  const [isFirstAppearance, setIsFirstAppearance] = useState<boolean>(true);
  const { elementRef, isElementVisible } = useOnElementVisible();

  useEffect(() => {
    /**
     * We only track listing views for its first appearance
     * e.g., if a user scrolls up and down, the listing is only counted once
     */
    const isElementVisibleAndFirstAppearance =
      isElementVisible && isFirstAppearance && !elementRef.current?.ariaHidden;

    if (isElementVisibleAndFirstAppearance) setIsFirstAppearance(false);

    handleListingView(isElementVisibleAndFirstAppearance);
  }, [isElementVisible, isFirstAppearance, handleListingView, elementRef]);

  return { elementRef };
}

function useOnElementVisible(): {
  elementRef: RefObject<HTMLDivElement>;
  isElementVisible: boolean;
} {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isElementVisible, setIsElementVisible] = useState<boolean>(false);

  useEffect(() => {
    //only count view when 100% (1.0) of the listing is visible within viewport
    const options = {
      threshold: 1.0,
    };

    const currentElement = elementRef.current;
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      setIsElementVisible(entry.isIntersecting);
    }, options);

    if (currentElement) {
      intersectionObserver.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        intersectionObserver.unobserve(currentElement);
      }
    };
  }, []);

  return { elementRef, isElementVisible };
}
